import type { WorkoutEntryGroup } from '@heraion/shared';

import type { DraftEntry } from './state/loggerDraft';

// Pure superset-fusion helpers for the Logger (Track R). Extracted out of
// LoggerScreen's body so the unit/group derivations are unit-testable in
// isolation. No React, no side effects — all functions of draft.entries.

export type GroupCatalogEntry = {
  id: string;
  kind: 'superset' | 'circuit';
  index: number;
};

// Displayed label for a group: a user-given name wins (inbox 963ca6f2 §1,
// editable superset name), else the unified "Superset N" (the "circuit" label
// is retired; index is unified workout-wide via buildGroupCatalog).
export function groupDisplayLabel(
  group: WorkoutEntryGroup | null,
  catalog: ReadonlyArray<GroupCatalogEntry>,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const index = groupIndexOf(group, catalog);
  if (!group || index === null) return t('mobile.group.standalone');
  if (group.name && group.name.trim().length > 0) return group.name.trim();
  return t('mobile.group.supersetN', { n: index });
}

// The workout-wide number of a group (the n of "Superset n"), whether or not
// the coach named it. null = no group, or one the catalog does not know.
// ONE place, so the chip's number and the label's n cannot drift (#520).
export function groupIndexOf(
  group: WorkoutEntryGroup | null,
  catalog: ReadonlyArray<GroupCatalogEntry>,
): number | null {
  if (!group) return null;
  return catalog.find((g) => g.id === group.id)?.index ?? null;
}

// 1.69.0 superset fusion: collapse draft.entries into logical "units".
// A solo unit is one entry; a group unit wraps every member of the
// same group.id in render order. Drag operates on UNITS — moving a
// group moves all its members together so the rounds stay coherent.
// Legacy web mirror: `7ae8e35e:apps/web/src/workout/WorkoutLog.tsx:3484-3520`.
export type Unit =
  | { kind: 'solo'; entryIdx: number }
  | {
      kind: 'group';
      groupId: string;
      groupKind: 'superset' | 'circuit';
      memberIdxs: number[];
    };

// Walk the draft once to materialise the group catalog: every distinct
// group id paired with a stable, 1-based index. The index is now a SINGLE
// sequential counter across all groups (Antenore 2026-06-01: the "circuit"
// label is retired — every grouping displays as "Superset N", so the number
// must be unique workout-wide, not per-kind, or a superset + a circuit would
// both read "Superset 1"). The `kind` field is still recorded for back-compat.
// The picker reads this so an entry can join an existing group by id.
export function buildGroupCatalog(entries: DraftEntry[]): GroupCatalogEntry[] {
  const out: GroupCatalogEntry[] = [];
  const seen = new Map<string, number>();
  let count = 0;
  for (const e of entries) {
    if (!e.group) continue;
    if (seen.has(e.group.id)) continue;
    const idx = ++count;
    seen.set(e.group.id, idx);
    out.push({ id: e.group.id, kind: e.group.kind, index: idx });
  }
  return out;
}

export function buildUnits(entries: DraftEntry[]): Unit[] {
  const out: Unit[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < entries.length; i++) {
    const e = entries[i];
    if (!e) continue;
    if (e.group) {
      if (seen.has(e.group.id)) continue;
      seen.add(e.group.id);
      const memberIdxs: number[] = [];
      for (let j = 0; j < entries.length; j++) {
        const m = entries[j];
        if (m?.group?.id === e.group.id) memberIdxs.push(j);
      }
      out.push({
        kind: 'group',
        groupId: e.group.id,
        groupKind: e.group.kind,
        memberIdxs,
      });
      continue;
    }
    out.push({ kind: 'solo', entryIdx: i });
  }
  return out;
}

// The ONE order of a draft: what buildUnits draws, flattened back to entries.
//
// PE-20. `buildUnits` and a raw `for (const e of draft.entries)` were two
// independent order computations over the same list, and they disagreed the
// moment a group's members were not physically contiguous. Clustering the
// array when a group is FORMED (set_group) fixes the drafts born here, but not
// the ones that arrive already scattered — `hydrateFromWorkout` (edit/repeat)
// and `planStarterHydration` map their entries in stored order, so a session
// saved under the old bug would re-save wrong forever, invisibly.
//
// Reading the save order from here makes the invariant hold BY CONSTRUCTION,
// for every producer, present and future: there is one order, and it is the
// one the athlete looked at.
export function renderOrder<T extends DraftEntry>(entries: T[]): T[] {
  const out: T[] = [];
  for (const u of buildUnits(entries)) {
    if (u.kind === 'solo') {
      const e = entries[u.entryIdx];
      if (e) out.push(e);
      continue;
    }
    for (const i of u.memberIdxs) {
      const e = entries[i];
      if (e) out.push(e);
    }
  }
  return out;
}
